import { Drawer } from '@iheartjane/reefer';
import { clsx } from 'clsx';
import { useEffect, useRef } from 'react';

import type { BottomSheetProps } from './bottomSheet.types';

import styles from './bottomSheet.module.css';

/**
 * `BottomSheet` slides up from the bottom of the screen over a dimmed overlay.
 * Built on reefer's `Drawer`, so tapping the overlay, pressing Esc, and
 * `useDrawerContext().onRequestClose` all close it with the slide-out animation.
 * The grab handle is decorative.
 */
export const BottomSheet = ({
  'aria-labelledby': ariaLabelledBy,
  'data-testid': testId = 'bottom-sheet',
  children,
  className,
  dimOverlay = true,
  height,
  id,
  onRequestClose,
  open,
  overlayClose = true,
  style,
}: BottomSheetProps) => {
  // Drawer's close waits for the slide-out and doesn't ignore repeat calls, so
  // a double tap or X then Esc would otherwise reach `onRequestClose` twice.
  const closedRef = useRef(false);

  useEffect(() => {
    if (open) closedRef.current = false;
  }, [open]);

  const handleRequestClose = () => {
    if (closedRef.current) return;
    closedRef.current = true;
    onRequestClose();
  };

  return (
    <Drawer
      aria-labelledby={ariaLabelledBy}
      className={clsx(
        styles['bottomSheet'],
        { [styles['bottomSheet__undimmed']]: !dimOverlay },
        className
      )}
      data-testid={testId}
      height={height}
      id={id}
      onRequestClose={handleRequestClose}
      open={open}
      overlayClose={overlayClose}
      placement="bottom"
      style={style}
    >
      <div
        aria-hidden="true"
        className={styles['handle']}
        data-testid={`${testId}-handle`}
      />
      {children}
    </Drawer>
  );
};
